import { useCallback, useEffect, useRef, useState } from 'react';

const AUTO_INTERVAL = 6000; // 自动轮播间隔

/**
 * 书籍轮播：一次居中展示一本书（封面 + 标题 + 作者），
 * 自动轮播 + 左右切换 + 圆点指示器。
 * books: [{ id, title, author, cover, desc, onClick }]
 */
export default function BookCarousel({ books = [] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const count = books.length;
  const safeIndex = count ? index % count : 0;

  const goTo = useCallback((next) => {
    if (!count) return;
    setIndex(((next % count) + count) % count);
  }, [count]);

  const next = useCallback(() => goTo(safeIndex + 1), [goTo, safeIndex]);
  const prev = useCallback(() => goTo(safeIndex - 1), [goTo, safeIndex]);

  // 自动轮播，悬停暂停
  useEffect(() => {
    if (paused || count <= 1) return undefined;
    timerRef.current = setInterval(() => {
      setIndex((cur) => (cur + 1) % count);
    }, AUTO_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [paused, count]);

  if (!count) return null;
  const book = books[safeIndex];

  return (
    <div
      className="academy-book-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button type="button" className="academy-book-arrow prev" onClick={prev} aria-label="上一本">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      <button type="button" className="academy-book-slide" key={book.id} onClick={book.onClick}>
        <div className="academy-book-cover">
          <img src={book.cover} alt={`${book.title} 封面`} />
        </div>
        <div className="academy-book-info">
          <span className="academy-book-tag">推荐阅读</span>
          <h3>{book.title}</h3>
          <span className="academy-book-author">{book.author}</span>
          {book.desc && <p className="academy-book-desc">{book.desc}</p>}
          <span className="academy-book-cta">查看详情 →</span>
        </div>
      </button>

      <button type="button" className="academy-book-arrow next" onClick={next} aria-label="下一本">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
      </button>

      {count > 1 && (
        <div className="academy-book-dots" role="tablist" aria-label="书籍切换">
          {books.map((b, i) => (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={i === safeIndex}
              className={`academy-book-dot${i === safeIndex ? ' active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`第 ${i + 1} 本`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
